"use client";

import { IMAGE_VARIANTS } from "@/lib/image-variants.generated";

/**
 * A landing <img> that prefers the AVIF/WebP siblings built by
 * `scripts/build-static-image-variants.mjs`, falling back to the original PNG.
 *
 * The landing shipped 1.23MB of raw PNG to every first-time visitor —
 * carousel-editor-light.png alone was 770KB for a 520px-tall slot, and the nav
 * wordmark 75KB to paint 28 pixels of logo — on a page whose HTML answers in
 * under 200ms. AVIF takes the same set to roughly 60KB.
 *
 * Not next/image: these are a fixed handful of build-time assets, and a file on
 * disk cannot fail the way a runtime optimiser can on a VPS. It also keeps the
 * hero a plain <img> that the preload scanner can start before hydration.
 *
 * A source with no manifest entry renders as the bare PNG rather than pointing
 * a srcset at variants that were never written, so adding a mockup without
 * rerunning the script degrades in bytes, never in correctness.
 */

interface MockupPictureProps {
  /** Path under /public, e.g. "/mockups/hero-phones-light.png". */
  src: string;
  alt: string;
  /** `sizes` for the srcset — how wide the image paints at each breakpoint. */
  sizes: string;
  className?: string;
  /** Set on the LCP image only; everything below the fold stays lazy. */
  priority?: boolean;
}

/** "/mockups/a.png" + 720 + "avif" → "/mockups/a-720w.avif" */
function variant(src: string, width: number, ext: string): string {
  return `${src.replace(/\.png$/, "")}-${width}w.${ext}`;
}

function srcSet(src: string, widths: readonly number[], ext: string): string {
  return widths.map((w) => `${variant(src, w, ext)} ${w}w`).join(", ");
}

export function MockupPicture({
  src,
  alt,
  sizes,
  className,
  priority = false,
}: MockupPictureProps) {
  const entry = IMAGE_VARIANTS[src];

  const img = (
    // eslint-disable-next-line @next/next/no-img-element -- see file header
    <img
      src={src}
      alt={alt}
      className={className}
      // Intrinsic size reserves the box so the image cannot shift the page as
      // it arrives; the className still governs how big it actually paints.
      width={entry?.width}
      height={entry?.height}
      // The hero is the LCP element: fetch it eagerly and early. Everything
      // else sits below the fold and can wait for the viewport, which is most
      // of the point of shrinking these at all.
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding={priority ? "sync" : "async"}
    />
  );

  if (!entry) return img;

  return (
    <picture>
      <source type="image/avif" srcSet={srcSet(src, entry.widths, "avif")} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(src, entry.widths, "webp")} sizes={sizes} />
      {img}
    </picture>
  );
}
